<template>
  <div
    class="table-item"
    :class="{
      'table-item--redesign': redesign,
      'table-item--compact': redesign && compact,
      'table-item--static': redesign && staticRows,
    }"
  >
    <slot name="title" />
    <div class="table-item__frame">
      <table
        :class="{ 'table-with-fixed-layout': isFixedLayout }"
        :aria-label="tableAriaLabel || 'Data table'"
        :aria-describedby="tableAriaDescribedby"
        role="table"
      >
        <caption v-if="tableCaption || $slots.caption" class="sr-only">
          <slot name="caption">{{ tableCaption }}</slot>
        </caption>
        <thead>
          <tr v-if="table.headers && table.headers.length" role="row">
            <template v-for="headerItem of table.headers" :key="headerItem">
              <th
                :class="[getClass(headerItem), { 'bf-th-sortable': isSortableHeader(headerItem) }]"
                :colspan="headerItem.colspan ? headerItem.colspan : '1'"
                :style="{
                  width:
                    headerItem.widthFixed ??
                    (headerItem.widthPercent ? headerItem.widthPercent + '%' : 100 / table.headers.length + '%'),
                }"
                :aria-label="headerItem.ariaLabel"
                :aria-sort="getAriaSort(headerItem)"
                scope="col"
                role="columnheader"
                @click="onHeaderCellClick(headerItem)"
              >
                <!-- Sortable header in the design-system variant: a real button, so it is
                     keyboard-reachable and announced as a control. The th keeps aria-sort. -->
                <button
                  v-if="isSortableHeader(headerItem)"
                  type="button"
                  class="bf-th-sort"
                  @click="$emit('tableHeaderClicked', headerItem)"
                >
                  {{ headerItem.title }}
                  <span class="bf-th-sort__caret" aria-hidden="true">{{ getSortCaret(headerItem) }}</span>
                </button>

                <template v-else>
                  {{ headerItem.title }}
                  <span v-if="headerItem.sorted && headerItem.sorted === 'asc'" class="arrow-up ml-2" />
                  <span v-if="headerItem.sorted && headerItem.sorted === 'desc'" class="arrow-down ml-2" />
                </template>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, index) of table.body" :key="`table-row-${index}`">
            <tr role="row" :class="[addClassErrorIfRowHasError(row), { 'bf-row--expanded': isRowExpanded(index) }]">
              <td
                v-for="(item, indexRow) of row"
                :key="`table-item-${item.type}-${indexRow}`"
                :colspan="item.colspan ? item.colspan : '1'"
                :rowspan="item.rowspan ? item.rowspan : '1'"
                :class="[
                  getClass(item),
                  {
                    'bf-td-name': redesign && indexRow === getNameCellIndex(row),
                    'bf-td-number': redesign && table.headers[indexRow]?.numeric,
                    'bf-td-expand': redesign && item.type === 'expand',
                  },
                ]"
                :data-label="redesign ? getDataLabel(indexRow) : undefined"
                role="cell"
              >
                <!-- A cell the page renders itself, through the scoped `cell-<slot>` slot — for content
                     no other cell type spells (a catalogue row's tags, a figure with a sub-line, a pair
                     of text actions). The cell keeps every class and data-label above. -->
                <slot v-if="item.type === 'slot'" :name="`cell-${item.slot}`" :item="item" :row="row" :index="index" />
                <!-- eslint-disable vue/no-v-html -- sanitized via sanitizeHtml() -->
                <div
                  v-else-if="item.type === 'value' && redesign && indexRow === getNameCellIndex(row)"
                  class="bf-cell-name"
                >
                  <!-- `custom` so the anchor can carry the sanitised markup: v-html cannot sit on the
                       RouterLink component itself. `navigate` keeps the router's own modifier-key
                       handling, so Cmd/Ctrl/middle click still open a new tab. -->
                  <RouterLink v-if="item.to" v-slot="{ href, navigate }" :to="item.to" custom>
                    <a
                      :href="href"
                      class="bf-cell-name__title bf-cell-name__title--link"
                      :title="item.tooltip"
                      @click="navigate"
                      v-html="sanitizeHtml(item.value)"
                    />
                  </RouterLink>
                  <button
                    v-else-if="item.fun"
                    type="button"
                    class="bf-cell-name__title bf-cell-name__title--link"
                    :title="item.tooltip"
                    @click="item.fun"
                    v-html="sanitizeHtml(item.value)"
                  />
                  <span v-else class="bf-cell-name__title" :title="item.tooltip" v-html="sanitizeHtml(item.value)" />
                  <span v-if="item.caption" class="bf-cell-name__sub">{{ item.caption }}</span>
                </div>
                <div
                  v-else-if="item.type === 'value'"
                  :role="item.fun ? 'button' : undefined"
                  @click="item.fun"
                  v-html="sanitizeHtml(item.value)"
                />
                <!-- eslint-enable vue/no-v-html -->
                <BfStatusChip
                  v-else-if="item.type === 'chips' && redesign"
                  :tone="item.color"
                  :variant="item.variant"
                  :label="item.value"
                  :solid="item.solid"
                  :aria-label="`Status: ${item.value}`"
                />
                <BaseChips
                  v-else-if="item.type === 'chips'"
                  :content="item.value"
                  :color="item.color"
                  class="size12-weight500"
                  :aria-label="`Status: ${item.value}`"
                />
                <CheckboxItem
                  v-else-if="item.type === 'checkbox'"
                  :aria-required="true"
                  :model-value="true"
                  @update:modelValue="toggleCheckbox(item)"
                />
                <div
                  v-else-if="item.type === 'action'"
                  class="d-flex align-center"
                  :class="{
                    'justify-start': !item.justify,
                    'justify-center': item?.justify === 'center',
                    'justify-end': item?.justify === 'right',
                  }"
                  role="group"
                  :aria-label="`Actions for row ${index + 1}`"
                >
                  <div
                    v-if="item.buttons?.length"
                    class="d-flex align-center table-item__button-wrapper"
                    :class="{
                      'justify-start': !item.justify,
                      'justify-center': item?.justify === 'center',
                      'justify-end': item?.justify === 'right',
                    }"
                  >
                    <template v-for="(button, indexButton) of item.buttons" :key="`table-button-action-${indexButton}`">
                      <BaseTooltip
                        v-if="item.withTooltips"
                        :is-right-position="true"
                        :is-light-mode="true"
                        :is-with-max-content="true"
                        :text="`Service ID: ${button.payload}`"
                      >
                        <ButtonItem
                          :button-size="button.size ? button.size : 'content'"
                          :button-color="button.color"
                          :class="$baseScript.getMargin(item.buttons, Number(indexButton))"
                          class="mb-2"
                          :disabled="button.disabled ?? false"
                          @click="button.fun(button.payload)"
                        >
                          {{ button.content }}
                        </ButtonItem>
                      </BaseTooltip>
                      <ButtonItem
                        v-else
                        :button-size="button.size ? button.size : 'content'"
                        :button-color="button.color"
                        :class="$baseScript.getMargin(item.buttons, Number(indexButton))"
                        class="mb-2"
                        :disabled="button.disabled ?? false"
                        @click="button.fun(button.payload)"
                      >
                        {{ button.content }}
                      </ButtonItem>
                    </template>
                  </div>
                  <!-- The design system's icon row action (12b, DS 1.1.124): a real <button> with
                       an accessible name, the 28px box and an orange EDGE on hover. BaseIcon supplies
                       only the glyph, so it takes no tabindex and no hover of its own — the button
                       owns the target, the state and the semantics. The group's gap replaces the
                       per-icon margin utilities the legacy branch below still uses. -->
                  <div
                    v-if="item.type === 'action' && item.icons?.length && redesign"
                    class="bf-row-actions"
                    :class="{ 'ml-md-10': item.buttons?.length }"
                  >
                    <template v-for="(icon, indexIcon) of item.icons" :key="`table-icon-action-${indexIcon}`">
                      <RouterLink
                        v-if="icon.to"
                        :to="icon.to"
                        class="bf-row-action bf-row-action--icon-only"
                        :aria-label="icon.label"
                        :title="icon.label"
                      >
                        <BaseIcon :icon-name="icon.name" :width="icon.width" :height="icon.height" is-without-hover />
                      </RouterLink>
                      <button
                        v-else
                        type="button"
                        class="bf-row-action bf-row-action--icon-only"
                        :aria-label="icon.label"
                        :title="icon.label"
                        :disabled="icon.disabled ?? false"
                        @click="icon.fun?.(icon.payload)"
                      >
                        <BaseIcon :icon-name="icon.name" :width="icon.width" :height="icon.height" is-without-hover />
                      </button>
                    </template>
                  </div>

                  <div
                    v-else-if="item.type === 'action' && item.icons?.length"
                    :class="{ 'ml-md-10': item.buttons?.length, 'd-flex align-center': item.icons?.length > 1 }"
                  >
                    <BaseIcon
                      v-for="(icon, indexIcon) of item.icons"
                      :key="`table-icon-action-${indexIcon}`"
                      :icon-name="icon.name"
                      :icon-color="icon.color"
                      :width="icon.width"
                      :height="icon.height"
                      :class="icon.cssClass ? icon.cssClass : $baseScript.getMargin(item.icons, Number(indexIcon))"
                      class="cursor-pointer mb-2"
                      :disabled="icon.disabled ?? false"
                      tabindex="0"
                      @iconClick="icon.fun?.(icon.payload)"
                      @keydown.enter="icon.fun?.(icon.payload)"
                    />
                  </div>
                </div>
                <!-- Disclosure of an expandable row (12b, DS 1.1.69). The toggle ONLY toggles: an
                     expandable row carries no actions cell, because the row's actions live in the
                     detail below it. The chevron is inline rather than a BaseIcon so it can take
                     the button's `currentColor` and rotate with `aria-expanded`. -->
                <button
                  v-else-if="item.type === 'expand'"
                  type="button"
                  class="bf-expand-toggle"
                  :aria-label="item.label"
                  :aria-expanded="isRowExpanded(index)"
                  :aria-controls="detailRowId(index)"
                  @click="toggleRow(index)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <div v-else-if="item.type === 'switch'" class="">
                  <SwitchItem
                    :model-value="item.value"
                    :disabled="item.disabled ?? false"
                    @update:modelValue="item.fun()"
                  />
                </div>
              </td>
            </tr>
            <!-- The detail, bracketed by the row above it. `hidden` rather than v-if so the panel
                 keeps its identity for aria-controls and is not rebuilt on every toggle. -->
            <tr v-if="hasDetail(row)" :id="detailRowId(index)" class="bf-detail-row" :hidden="!isRowExpanded(index)">
              <td :colspan="table.headers.length" data-label="">
                <div class="bf-detail">
                  <slot name="detail" :row="row" :index="index" />
                </div>
              </td>
            </tr>
            <tr v-if="addErrorTextIfRowHasError(row)" class="error-text-row size12-weight500">
              <td :colspan="table.headers.length">
                {{ addErrorTextIfRowHasError(row) }}
              </td>
            </tr>
          </template>

          <tr v-if="table.body.length === 0">
            <td :colspan="table.headers.length" class="text-align-center bf-td-empty" :class="{ 'pa-5': !redesign }">
              <slot name="emptyDataContent" />
            </td>
          </tr>

          <!-- Closing lines of a document table. They live in the tbody, not a tfoot: a tfoot
               escapes the mobile stacking rules, and the label cell has to inherit the body's
               column widths to line its figure up with the amounts above it. -->
          <tr
            v-for="(summaryRow, summaryIndex) of summaryRowList"
            :key="`table-summary-${summaryIndex}`"
            class="bf-summary-row"
            :class="{
              'bf-summary-row--first': summaryIndex === 0,
              'bf-summary-row--total': summaryRow.total,
              'bf-summary-row--accent': summaryRow.total && summaryRow.accent,
            }"
            role="row"
          >
            <td class="bf-summary-row__label" :colspan="summaryLabelSpan" data-label="" role="cell">
              {{ summaryRow.label }}
            </td>
            <td class="bf-td-number" data-label="" role="cell">{{ summaryRow.value }}</td>
          </tr>
        </tbody>
      </table>

      <!-- The design system's pager placement: inside the frame, after the table, so the
           frame's border and radius enclose it and its top hairline reads as the table's last
           rule. Opt-in — a page that keeps its own footer row simply leaves this slot empty. -->
      <slot name="pager" />
    </div>
    <slot name="prepend" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import {
    BodyItemsI,
    BodyValueItemI,
    FontParametersI,
    HeaderItemI,
    TableItemI,
    TableSummaryRowI,
  } from '@/typescript/types/ui-components/TableItem'
  import { sanitizeHtml } from '@/utils'

  /* Instance counter, so two tables on one page cannot mint the same aria-controls id. A plain
     module counter rather than useId(): this component is Options API. */
  let tableInstanceCount = 0

  export default defineComponent({
    name: 'BaseTable',
    props: {
      withActions: {
        type: Boolean,
        default: false,
      },
      isSmallPadding: {
        type: Boolean,
        default: false,
      },
      isFixedLayout: {
        type: Boolean,
        default: false,
      },
      /** Opt-in Bahnhof design-system styling (hairline frame, uppercase headers, status chips, mobile card stacking). Off by default so existing pages are unaffected. */
      redesign: {
        type: Boolean,
        default: false,
      },
      /**
       * Document density: ~36px rows against the default ~49px. A density, not a second
       * variant — it only re-declares the two paddings the frame already exposes. Neutralised
       * below the stacking breakpoint, where rows are cards and touch targets. Redesign only.
       */
      compact: {
        type: Boolean,
        default: false,
      },
      /**
       * Drops the row hover tint. The tint is an affordance, so a table where no row is
       * clickable — invoice line items, transactions — should not promise a target that is not
       * there. Named `staticRows` because `static` is a reserved word in strict mode; it is the
       * design system's `--static`. Redesign only.
       */
      /**
       * Expandable rows: one open at a time. The card's `data-exclusive` (12b, DS 1.1.69). Off by
       * default, so several details can stand open together.
       */
      exclusiveDetail: {
        type: Boolean,
        default: false,
      },
      staticRows: {
        type: Boolean,
        default: false,
      },
      /**
       * Drops the name-cell treatment entirely, so every cell is body type at regular weight.
       * The bold title marks the column that NAMES the row's object; a document table whose
       * leading column is a plain attribute — a transaction's date, say — has no such column,
       * and defaulting to the first one bolds a date. Redesign only.
       */
      noNameCell: {
        type: Boolean,
        default: false,
      },
      /** Closing lines under the body — sub total, VAT, balance due. Redesign only. */
      summaryRows: {
        type: Array as PropType<TableSummaryRowI[]>,
        default: () => [],
      },
      table: {
        type: Object as PropType<TableItemI>,
        default: () => ({
          title: 'Table',
          headers: ['Value head', 'Chips head', 'Action button head', 'Action icon head'],
          body: [
            [
              {
                type: 'value',
                value: 'Value',
              },
              {
                type: 'chips',
                value: 'active',
                color: 'orange',
              },
              {
                type: 'action',
                icons: [
                  {
                    name: 'icon-warning.svg',
                    color: 'blue',
                    width: '24px',
                    height: '24px',
                    fun: () => console.log('Icon action'),
                  },
                ],
              },
              {
                type: 'action',
                buttons: [
                  {
                    content: 'Action button',
                    color: '#3b889f',
                    fun: () => console.log('Button action'),
                  },
                  {
                    content: 'Action button',
                    color: '#3b889f',
                    fun: () => console.log('Button action'),
                  },
                ],
              },
            ],
            [
              {
                type: 'value',
                value: 'Value',
              },
              {
                type: 'chips',
                value: 'active',
                color: 'orange',
              },
              {
                type: 'action',
                icons: [
                  {
                    name: 'icon-warning.svg',
                    color: 'blue',
                    width: '24px',
                    height: '24px',
                    fun: () => console.log('Icon action'),
                  },
                ],
              },
              {
                type: 'action',
                buttons: [
                  {
                    content: 'Action button',
                    color: '#3b889f',
                    fun: () => console.log('Button action'),
                  },
                ],
              },
            ],
          ],
        }),
      },
      tableCaption: {
        type: String,
        default: '',
        required: false,
      },
      tableAriaLabel: {
        type: String,
        default: '',
        required: false,
      },
      tableAriaDescribedby: {
        type: String,
        default: '',
        required: false,
      },
    },
    emits: ['tableHeaderClicked', 'toggleCheckbox'],
    data() {
      return {
        /** Indices of the rows whose detail is open. */
        expandedRows: [] as number[],
        detailIdPrefix: `bf-table-${(tableInstanceCount += 1)}`,
      }
    },
    computed: {
      /** Summary rows are a design-system treatment; legacy tables never render them. */
      summaryRowList(): TableSummaryRowI[] {
        return this.redesign ? this.summaryRows : []
      },
      /** The label spans every column but the last, so its figure lands under the amounts. */
      summaryLabelSpan(): number {
        return Math.max(this.table.headers.length - 1, 1)
      },
    },
    methods: {
      sanitizeHtml,
      /** A row is expandable when it carries the disclosure cell; the detail then comes from the slot. */
      hasDetail(row: BodyItemsI[]): boolean {
        return this.redesign && !!this.$slots.detail && row.some(item => item.type === 'expand')
      },
      detailRowId(index: number): string {
        return `${this.detailIdPrefix}-detail-${index}`
      },
      isRowExpanded(index: number): boolean {
        return this.expandedRows.includes(index)
      },
      toggleRow(index: number) {
        if (this.isRowExpanded(index)) {
          this.expandedRows = this.expandedRows.filter(open => open !== index)
          return
        }
        this.expandedRows = this.exclusiveDetail ? [index] : [...this.expandedRows, index]
      },
      getDataLabel(index: number) {
        const header = this.table.headers?.[index]
        return header?.dataLabel ?? header?.title ?? ''
      },
      /**
       * Which cell carries the bold title treatment: the one flagged `isNameCell`, else the first
       * column — or none at all under `noNameCell`, which -1 gives us since no index matches it.
       */
      getNameCellIndex(row: any[]) {
        if (this.noNameCell) return -1
        const flagged = row.findIndex((item: any) => item.isNameCell)
        return flagged === -1 ? 0 : flagged
      },
      getPosition(val: string = '') {
        switch (val) {
          case 'left':
            return 'text-align-left'
          case 'center':
            return 'text-align-center'
          case 'right':
            return 'text-align-right'
          default:
            return ''
        }
      },
      getFontsParameters(val: FontParametersI | undefined) {
        if (val) {
          return `size${val.size ?? 14}-weight${val.weight ?? 400} line-height20`
        } else return ''
      },
      /**
       * A column is sortable when it carries a `label` — the key the page sorts by. Only the
       * design-system variant promotes it to a button; legacy tables keep the th click.
       */
      isSortableHeader(headerItem: HeaderItemI): boolean {
        return this.redesign && Boolean(headerItem.label)
      },
      /** `aria-sort` belongs to the th: the active direction, or `none` while it is sortable. */
      getAriaSort(headerItem: HeaderItemI): 'ascending' | 'descending' | 'none' | undefined {
        if (!this.isSortableHeader(headerItem)) return undefined
        if (headerItem.sorted === 'asc') return 'ascending'
        if (headerItem.sorted === 'desc') return 'descending'
        return 'none'
      },
      /**
       * The caret keeps its box on every sortable column so headers do not shift; CSS hides it
       * until the column is the sorted one.
       */
      getSortCaret(headerItem: HeaderItemI): string {
        return headerItem.sorted === 'desc' ? '▼' : '▲'
      },
      /** The sortable variant emits from its own button, so the cell must stay silent. */
      onHeaderCellClick(headerItem: HeaderItemI): void {
        if (this.isSortableHeader(headerItem)) return
        this.$emit('tableHeaderClicked', headerItem)
      },
      getClass(item: any) {
        let classList = ''
        classList += `${this.getFontsParameters({
          size: item.size,
          weight: item.weight,
        })} `
        classList += item.position ? this.getPosition(item.position) : 'text-align-left'
        classList += item.label ? ' cursor-pointer' : ''
        classList += item.color ? item.color : ''
        classList += item.background ? item.background : ''
        classList += item.width ? ` w-${item.width}` : ''
        classList += this.isSmallPadding ? ` small-padding` : ''
        return classList
      },

      addClassErrorIfRowHasError(row: any[]) {
        const hasError = row.some(item => item.isError)
        return hasError ? 'row-with-error' : ''
      },

      addErrorTextIfRowHasError(row: any[]) {
        const itemWithError = row.find(item => item.isError)
        return itemWithError?.textError ?? ''
      },
      toggleCheckbox(item: BodyValueItemI) {
        this.$emit('toggleCheckbox', item.value)
      },
    },
  })
</script>

<style scoped lang="scss">
  @for $i from 1 through 99 {
    .w-#{$i} {
      width: #{$i}#{'%'} !important;
    }
  }

  .table-item {
    width: 100%;

    table {
      width: 100%;
      border-collapse: collapse;
    }

    .table-with-fixed-layout {
      table-layout: fixed;

      td {
        word-wrap: break-word;
      }
    }

    &__button-wrapper {
      width: 100%;
    }

    .row-with-error {
      border: 1px solid var(--color-text-red);
    }

    .error-text-row {
      color: var(--color-text-red);
      background-color: #ffffff !important;

      td {
        padding: 8px 8px;
      }
    }
  }

  /* Legacy 4-column width assumption. Redesign tables size their columns from the header
     `widthPercent` values instead, and fold into full-width cards on mobile. */
  .table-item:not(.table-item--redesign) .table-with-fixed-layout td {
    width: calc(100% / 4);
  }

  /* Legacy table appearance — skipped when the opt-in Bahnhof variant is active */
  .table-item:not(.table-item--redesign) table thead th {
    text-wrap: nowrap;
    padding: 16px 8px;
    background-color: #fafafa;
    color: #666666;
  }

  .table-item:not(.table-item--redesign) table tbody td {
    padding: 12px 12px;
  }

  .table-item:not(.table-item--redesign) table tbody .small-padding {
    padding: 6px 16px;
  }

  .table-item:not(.table-item--redesign) table tbody tr {
    background-color: #ffffff;
  }

  .table-item:not(.table-item--redesign) table tbody tr:nth-child(even) {
    background-color: #fafafa;
  }

  .table-item:not(.table-item--redesign) table th,
  .table-item:not(.table-item--redesign) table td {
    padding: 5px;
  }

  /* ── Bahnhof design-system variant ─────────────────────────────────────── */
  /* One pair of paddings for the whole block: they inherit into every th/td, so the header
     row, the body cells and the sort button can never drift apart. 14/18px is the design
     system's own off-grid exception, declared once here rather than promoted to a token. */
  /* The fold is a CONTAINER query in the design system (12b): the table stacks at 700px of its
     OWN width, not the viewport's, so a table in a narrow column stacks on a wide screen too.
     `.table-item` is the query context — the card's `.data-table-container` — and the frame below
     is its `.data-table-wrapper`. Safe for the docked pager's page-size menu: that is teleported to
     <body>, so `contain` never reaches it. */
  .table-item--redesign {
    container-type: inline-size;
  }

  .table-item--redesign .table-item__frame {
    --bf-data-table-padding-vertical: 14px;
    --bf-data-table-padding-horizontal: 18px;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    overflow-x: auto;
    overflow-y: hidden;
    background: var(--bf-white);
  }

  .table-item--redesign thead tr {
    background: var(--bf-blue-light);
  }

  .table-item--redesign thead th {
    text-align: left;
    padding: var(--bf-data-table-padding-vertical) var(--bf-data-table-padding-horizontal);
    font-family: var(--bf-font-display);
    font-size: 11px;
    font-weight: var(--bf-fw-bold);
    /* Declared, not inherited: getFontsParameters() stamps a `line-height20` utility on
       every header cell, which is 7px taller than the design system's header row. */
    line-height: var(--bf-lh-snug);
    color: var(--bf-dusky-1);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    border-bottom: 1px solid var(--bf-line);
    white-space: nowrap;
  }

  /* Sortable header: the cell gives up its padding to the button, which takes over the whole
     hit area so the label and the caret are one target. Type is inherited, not restated. */
  .table-item--redesign thead th.bf-th-sortable {
    padding: 0;
  }

  .table-item--redesign .bf-th-sort {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: var(--bf-data-table-padding-vertical) var(--bf-data-table-padding-horizontal);
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    background: none;
    border: 0;
    border-radius: var(--bf-r-sm);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .table-item--redesign .bf-th-sort:hover {
    color: var(--bf-orange-dark);
  }

  /* Same inset ring as the design system's tabs, so the header row never grows on focus. */
  .table-item--redesign .bf-th-sort:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -2px;
  }

  /* The caret keeps its box on every sortable column — only its visibility changes, so
     switching the sorted column never shifts a header label sideways. */
  .table-item--redesign .bf-th-sort__caret {
    font-size: 11px;
    line-height: 1;
    color: var(--bf-orange-dark);
    visibility: hidden;
  }

  .table-item--redesign thead th[aria-sort='ascending'] .bf-th-sort__caret,
  .table-item--redesign thead th[aria-sort='descending'] .bf-th-sort__caret {
    visibility: visible;
  }

  /* Sortable header: the cell gives up its padding to the button, which takes over the whole
     hit area so the label and the caret are one target. Type is inherited, not restated. */
  .table-item--redesign thead th.bf-th-sortable {
    padding: 0;
  }

  .table-item--redesign .bf-th-sort {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: var(--bf-data-table-padding-vertical) var(--bf-data-table-padding-horizontal);
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    background: none;
    border: 0;
    border-radius: var(--bf-r-sm);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .table-item--redesign .bf-th-sort:hover {
    color: var(--bf-orange-dark);
  }

  /* Same inset ring as the design system's tabs, so the header row never grows on focus. */
  .table-item--redesign .bf-th-sort:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -2px;
  }

  /* The caret keeps its box on every sortable column — only its visibility changes, so
     switching the sorted column never shifts a header label sideways. */
  .table-item--redesign .bf-th-sort__caret {
    font-size: 11px;
    line-height: 1;
    color: var(--bf-orange-dark);
    visibility: hidden;
  }

  .table-item--redesign thead th[aria-sort='ascending'] .bf-th-sort__caret,
  .table-item--redesign thead th[aria-sort='descending'] .bf-th-sort__caret {
    visibility: visible;
  }

  .table-item--redesign tbody td {
    padding: var(--bf-data-table-padding-vertical) var(--bf-data-table-padding-horizontal);
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    color: var(--bf-deep-blue);
    border-bottom: 1px solid var(--bf-grey-light);
    transition: background var(--bf-dur-fast) var(--bf-ease);
  }

  /* Dates and amounts: tabular figures so the columns line up, no wrapping, and a step back
     from the body colour — the identifier column and the status are what the eye should land
     on, so the supporting figures recede. The design draws these cells at the *tertiary* step
     (--bf-fg-3, #919baf), which is 2.8:1 on white and fails WCAG AA for text a customer has
     to read, so this takes the secondary step (6.33:1) instead: the same intent, one shade
     darker. The design system's own pager count makes the same choice. Opt-in per column via
     `numeric` on the header. */
  .table-item--redesign tbody td.bf-td-number {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: var(--bf-fg-2);
  }

  /* getClass() stamps `text-align-*` on every cell from its `position`, but that utility has
     no declaration anywhere in the app's global layer — so a column asking to be right-aligned
     never was. Declared here rather than globally: the legacy variant has lived without it for
     years, and the `thead th { text-align: left }` rule above would out-specify a bare utility
     class anyway. */
  .table-item--redesign thead th.text-align-right,
  .table-item--redesign tbody td.text-align-right {
    text-align: right;
  }

  .table-item--redesign tbody tr:last-child td {
    border-bottom: none;
  }

  .table-item--redesign tbody tr:hover td {
    background: var(--bf-blue-light);
  }

  /* Empty state: a row, not a replacement for the table — the frame, the header row and
     anything below stay in place. The cell drops the `pa-5` utility in this variant so the
     padding can come from here, where the design system declares it. */
  .table-item--redesign tbody td.bf-td-empty {
    padding: var(--bf-sp-5) var(--bf-sp-3);
    /* Declared, not left to the `text-align-center` utility the cell also carries — that one
       has no definition either. */
    text-align: center;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-3);
    letter-spacing: var(--bf-tr-nudge);
  }

  /* Nothing to hover: it is not a row of data. */
  .table-item--redesign tbody tr:hover td.bf-td-empty {
    background: var(--bf-white);
  }

  /* Service-name cell: bold title + optional ServiceID caption */
  .table-item--redesign .bf-cell-name {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 10px;
  }

  .table-item--redesign .bf-cell-name__title {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    /* A name can arrive as one long unbroken string — a support subject typed without spaces.
       The title is a flex item, so without `min-width: 0` it keeps its min-content width and
       spills across the next column under `table-layout: fixed`; `anywhere` lets the line break
       mid-token. Two lines is where it stops: past that a single row would set the height of the
       whole register. The full text stays reachable through the cell's `title` and the record. */
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    /* --bf-deep-blue, like every other body cell, not --bf-black: the design system's data
       table calls that out by name — black made the name column the only pure-black text
       in the table. */
    color: var(--bf-deep-blue);
    letter-spacing: var(--bf-tr-tight);
  }

  /* Clickable service name → routes to the service */
  .table-item--redesign .bf-cell-name__title--link {
    padding: 0;
    background: none;
    border: none;
    font-size: inherit;
    line-height: inherit;
    text-align: left;
    /* The name cell can be an <a> as well as a <button>; the anchor must not pick up the
       global link underline at rest. */
    text-decoration: none;
    cursor: pointer;
    transition: color var(--bf-dur-fast) var(--bf-ease);
  }

  /* A cell or row action can be an <a> (a `to` link). The global `a:focus` rule draws a blue outline
     and an underline on any focused anchor, which a Cmd/Ctrl-click (open in a new tab) leaves
     standing on this page. The keyboard ring stays — it is the :focus-visible rules below. */
  .table-item--redesign a.bf-cell-name__title--link:focus:not(:focus-visible),
  .table-item--redesign a.bf-row-action:focus:not(:focus-visible) {
    outline: none;
    text-decoration: none;
  }

  .table-item--redesign .bf-cell-name__title--link:hover {
    color: var(--bf-orange-dark);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .table-item--redesign .bf-cell-name__title--link:focus-visible {
    outline: 2px solid var(--bf-orange-light);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .table-item--redesign .bf-cell-name__sub {
    font-family: var(--bf-font-body);
    font-weight: var(--bf-fw-regular);
    font-size: var(--bf-fs-xs);
    color: var(--bf-dusky-2);
    font-variant-numeric: tabular-nums;
  }

  /* ---------- catalogue rows (slot cells) ----------
     A cell rendered through `cell-<slot>` carries the page's scope, not this component's, so its
     classes are reached with :slotted(). Same recipes as the DS data table's catalogue row. */

  /* Type only: colour, underline, hover and the <button> reset come from BfLink tone="neutral" on
     the same element — the name cell composes the Link block rather than restating it. */
  .table-item--redesign :slotted(.bf-cell-name__primary) {
    display: inline;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: var(--bf-tr-tight);
    text-align: left;
  }

  .table-item--redesign :slotted(.bf-cell-name__tags) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
  }

  /* The unit or period under a figure, at the tertiary step the design draws (#919baf). That is
     2.8:1 on white, below AA for body text — kept on purpose to match the design. */
  .table-item--redesign :slotted(.bf-cell-number__sub) {
    display: block;
    margin-top: 2px;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-2xs);
    font-weight: var(--bf-fw-regular);
    letter-spacing: var(--bf-tr-normal);
    color: var(--bf-fg-3);
  }

  /* A row's text actions beside a small primary button. */
  .table-item--redesign :slotted(.bf-row-actions--text) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--bf-sp-2);
  }

  /* ---------- toolbar (in the `title` slot, above the frame) ----------
     Filters lead, the result count trails. */
  .table-item--redesign :slotted(.bf-table-toolbar) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }

  .table-item--redesign :slotted(.bf-table-toolbar__filters) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  /* --bf-fg-2 for the same contrast reason as the number sub-line. */
  .table-item--redesign :slotted(.bf-table-toolbar__count) {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
  }

  .table-item--redesign :slotted(.bf-table-toolbar__count strong) {
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
  }

  /* Right-align the trailing action column */
  /* ---------- row action (bespoke, NOT a button variant) — 12b, DS 1.1.124 ----------
     The group wrapper is required: a display:flex <td> leaves table layout and the columns
     collapse. Justification stays with the cell's own `justify-*` class, so the group only
     spaces its children. */
  .table-item--redesign .bf-row-actions {
    display: flex;
    align-items: center;
    gap: var(--bf-sp-1);
  }

  .table-item--redesign .bf-row-action,
  .table-item--redesign :slotted(.bf-row-action) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    background: none;
    border: 0;
    border-radius: var(--bf-r-sm);
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: var(--bf-tr-nudge);
    line-height: 1.2;
    color: var(--bf-orange-dark);
    text-decoration: none;
    cursor: pointer;
  }

  /* The row's second action, when the orange belongs to the CTA beside it. Must follow the base
     .bf-row-action rule above: same specificity, so source order decides the colour. */
  .table-item--redesign :slotted(.bf-row-action--muted) {
    color: var(--bf-fg-2);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .table-item--redesign :slotted(.bf-row-action--muted:hover:not(:disabled)) {
    color: var(--bf-orange-dark);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Resting tone is --bf-fg-2, never --bf-fg-3: the tertiary step is this control's own :disabled
     colour, so resting and disabled would differ only by the cursor. Hover is an EDGE, not a fill —
     the row already answers the pointer with its own hover surface, and a second filled rectangle
     inside it is two surfaces for one pointer. The resting 1px transparent border is load-bearing:
     it keeps the 28px box from resizing when the edge is drawn, which is why border-color joins the
     transition in place of background-color. */
  .table-item--redesign .bf-row-action--icon-only {
    justify-content: center;
    width: 28px;
    height: 28px;
    /* Keeps the height the control contributes to the cell at the row's own line box; without it
       every row grows ~6px. The overhang sits inside the cell's 14px padding, so nothing is cut. */
    margin: -6px 0;
    gap: 0;
    border: 1px solid transparent;
    color: var(--bf-fg-2);
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      border-color var(--bf-dur-base) var(--bf-ease);
  }

  /* BaseIcon paints the glyph as its mask element's background, so `currentColor` is what carries
     the button's colour — and its transition — into the glyph. */
  .table-item--redesign .bf-row-action :deep(.icon i) {
    background-color: currentColor;
  }

  .table-item--redesign .bf-row-action--icon-only:hover:not(:disabled) {
    color: var(--bf-orange-dark);
    border-color: var(--bf-orange-dark);
    background: none;
    text-decoration: none;
  }

  .table-item--redesign .bf-row-action:focus-visible,
  .table-item--redesign :slotted(.bf-row-action:focus-visible) {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 2px;
  }

  .table-item--redesign .bf-row-action:disabled,
  .table-item--redesign :slotted(.bf-row-action:disabled) {
    color: var(--bf-fg-3);
    cursor: not-allowed;
    pointer-events: none;
    text-decoration: none;
  }

  .table-item--redesign .table-item__button-wrapper {
    width: auto;
  }

  /* Status chips live in BfStatusChip.vue so the design kit can render the same component. */

  /* Row action reskinned as the design's orange "Manage →" text link */
  .table-item--redesign :deep(.btn.aquamarin-btn-outline) {
    width: auto;
    padding: 2px 0;
    background: transparent;
    border: none;
    color: var(--bf-link);
    font-family: var(--bf-font-display);
    font-weight: var(--bf-fw-semibold);
    font-size: 13px;
    letter-spacing: 0.01em;
    white-space: nowrap;
  }

  .table-item--redesign :deep(.btn.aquamarin-btn-outline:not(:disabled):hover) {
    background: transparent;
    color: var(--bf-orange-dark);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Disabled action → muted, non-clickable grey text link (matches design's grey link) */
  .table-item--redesign :deep(.btn.aquamarin-btn-outline:disabled) {
    background: transparent;
    border: none;
    color: var(--bf-dusky-2);
    cursor: not-allowed;
  }

  /* ── Density: --compact ────────────────────────────────────────────────── */
  /* A density, not a second variant: anatomy, stacking, sortable headers, row actions
     and the empty state are all unchanged. The mechanism is the pair of paddings the
     frame already declares — they inherit into every th/td and the sort button, so
     re-declaring them here is the whole change. Rows measure ~36px against ~49px. */
  .table-item--compact .table-item__frame {
    --bf-data-table-padding-vertical: 8px;
    --bf-data-table-padding-horizontal: 14px;
  }

  .table-item--compact tbody td,
  .table-item--compact .bf-cell-name__title {
    font-size: var(--bf-fs-xs_5);
  }

  .table-item--compact tbody td.bf-td-empty {
    padding: var(--bf-sp-3);
  }

  /* ── --static: no row is clickable ─────────────────────────────────────── */
  /* The hover tint is an affordance. On a document table — invoice line items, a
     transaction list — no row links anywhere and no row carries an action, so the tint
     promises a target that is not there. Header sort buttons and any control inside a
     cell keep their own hover; only the row surface goes quiet. */
  .table-item--static tbody tr:hover td {
    background: transparent;
  }

  /* ── Summary rows ──────────────────────────────────────────────────────── */
  /* Sub total / VAT / balance due plus one emphasised total. They never hover: they are
     not rows of data. */
  .table-item--redesign tbody tr.bf-summary-row td,
  .table-item--redesign tbody tr.bf-summary-row:hover td {
    background: transparent;
    border-top: 1px solid var(--bf-grey-light);
    border-bottom: 0;
    font-family: var(--bf-font-body);
    color: var(--bf-fg-2);
  }

  /* The first summary line closes the body, so its rule is the table's, not a divider
     between two summary lines. */
  .table-item--redesign tbody tr.bf-summary-row--first td {
    border-top: 1px solid var(--bf-line);
  }

  .table-item--redesign .bf-summary-row__label {
    text-align: right;
  }

  /* Body cells get their alignment from the `position` utility getClass() stamps on them;
     a summary cell has no item behind it, so the trailing column is aligned here instead. */
  .table-item--redesign tbody tr.bf-summary-row td.bf-td-number {
    text-align: right;
  }

  /* Body font, not display: the amount column is tabular body type all the way down, and
     switching family on the last row breaks the column rather than emphasising it. Weight,
     size and the surface carry the emphasis instead. */
  .table-item--redesign tbody tr.bf-summary-row--total td,
  .table-item--redesign tbody tr.bf-summary-row--total:hover td {
    background: var(--bf-blue-light);
    border-top: 1px solid var(--bf-line);
    font-size: var(--bf-fs-base);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
  }

  /* --accent must come after --total: equal specificity, so source order decides. */
  .table-item--redesign tbody tr.bf-summary-row--accent td,
  .table-item--redesign tbody tr.bf-summary-row--accent:hover td {
    background: var(--bf-orange-ultralight);
  }

  /* ── Mobile (≤700px): each row folds into a stacked card ───────────────── */
  /* ---------- expandable rows — 12b, DS 1.1.69, tint dropped 1.1.72 ----------
     The open row takes NO surface tint: the rotated chevron and the detail itself are the state,
     and the row keeps the stronger --bf-line rule so the detail is bracketed by the same weight
     the header uses. */
  .table-item--redesign td.bf-td-expand {
    width: 1%;
    text-align: right;
    white-space: nowrap;
  }

  .table-item--redesign .bf-expand-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: -6px 0;
    padding: 0;
    border: 0;
    border-radius: var(--bf-r-sm);
    background: none;
    color: var(--bf-fg-3);
    cursor: pointer;
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      background-color var(--bf-dur-base) var(--bf-ease);
  }

  .table-item--redesign .bf-expand-toggle:hover {
    color: var(--bf-orange-dark);
    background: var(--bf-nav-active);
  }

  .table-item--redesign .bf-expand-toggle:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 2px;
  }

  .table-item--redesign .bf-expand-toggle svg {
    width: 16px;
    height: 16px;
    transition: transform var(--bf-dur-base) var(--bf-ease);
  }

  .table-item--redesign .bf-expand-toggle[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  .table-item--redesign tbody tr.bf-row--expanded td {
    border-bottom-color: var(--bf-line);
  }

  .table-item--redesign tbody tr.bf-detail-row:hover td {
    background: transparent;
  }

  .table-item--redesign tbody tr.bf-detail-row td {
    padding: 0;
    border-bottom: 1px solid var(--bf-line);
  }

  .table-item--redesign tbody tr.bf-detail-row:last-child td {
    border-bottom: 0;
  }

  .table-item--redesign .bf-detail {
    padding: 16px 18px;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    line-height: var(--bf-lh-normal);
    color: var(--bf-fg-2);
  }

  .table-item--redesign .bf-detail > :first-child {
    margin-top: 0;
  }

  .table-item--redesign .bf-detail > :last-child {
    margin-bottom: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .table-item--redesign .bf-expand-toggle svg {
      transition: none;
    }
  }

  @container (max-width: 700px) {
    /* `tbody tr` goes display:flex below, which outranks the UA sheet's [hidden]{display:none} —
       without this a closed detail row would still render. */
    .table-item--redesign tbody tr[hidden] {
      display: none;
    }

    /* Stacked: the toggle sits in the card foot like the actions cell, and the open card drops its
       bottom radii so the detail row continues the same box. */
    .table-item--redesign tbody td.bf-td-expand {
      width: auto;
      justify-content: flex-end;
      border-top: 1px solid var(--bf-line-2);
    }

    .table-item--redesign tbody td.bf-td-expand::before {
      display: none;
    }

    .table-item--redesign tbody tr.bf-row--expanded,
    .table-item--redesign tbody tr.bf-row--expanded:hover {
      margin-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .table-item--redesign tbody tr.bf-detail-row {
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    .table-item--redesign tbody tr.bf-detail-row td {
      display: block;
      padding: 0;
      text-align: left;
      border-bottom: 0;
    }

    .table-item--redesign tbody tr.bf-detail-row td::before {
      display: none;
    }

    /* Rows are cards now; a fixed column grid has nothing to lay out. */
    .table-item--redesign .table-with-fixed-layout {
      table-layout: auto;
    }

    .table-item--redesign .table-item__frame {
      border: none;
      background: transparent;
      border-radius: 0;
      overflow: visible;
    }

    .table-item--redesign thead {
      display: none;
    }

    .table-item--redesign table,
    .table-item--redesign tbody,
    .table-item--redesign tbody td {
      display: block;
      width: 100%;
    }

    /* Flex column so the title cell can be hoisted with `order` even when it isn't the first column */
    .table-item--redesign tbody tr {
      display: flex;
      flex-direction: column;
      width: 100%;
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg);
      background: var(--bf-white);
      margin-bottom: 14px;
      box-shadow: var(--bf-shadow-1);
      overflow: hidden;
    }

    .table-item--redesign tbody tr:hover td {
      background: var(--bf-white);
    }

    .table-item--redesign tbody td {
      border: none;
      padding: 9px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      text-align: right;
      font-size: var(--bf-fs-sm);
    }

    .table-item--redesign tbody td::before {
      content: attr(data-label);
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--bf-dusky-1);
      text-align: left;
      flex-shrink: 0;
      white-space: nowrap;
    }

    .table-item--redesign tbody td.bf-td-name {
      display: block;
      text-align: left;
      padding: 14px 16px;
      border-bottom: 1px solid var(--bf-grey-light);
    }

    .table-item--redesign tbody td.bf-td-name .bf-cell-name__title,
    .table-item--redesign tbody td.bf-td-name :slotted(.bf-cell-name__primary) {
      font-size: var(--bf-fs-base);
    }

    /* A figure with a sub-line: the sub-line takes its own line under the figure, still trailing. */
    .table-item--redesign tbody td.bf-td-number:has(.bf-cell-number__sub) {
      flex-wrap: wrap;
    }

    .table-item--redesign tbody td.bf-td-number :slotted(.bf-cell-number__sub) {
      flex: 1 0 100%;
      margin-top: 0;
      text-align: right;
    }

    .table-item--redesign tbody td.bf-td-name::before {
      display: none;
    }

    /* Empty state spans the whole card — no inline label, stays centred */
    .table-item--redesign tbody td.bf-td-empty {
      display: block;
      text-align: center;
    }

    .table-item--redesign tbody td.bf-td-empty::before {
      display: none;
    }

    /* Compact is a desktop density: below this breakpoint rows are cards and touch
       targets, so the modifier is neutralised rather than compounded. */
    .table-item--compact .table-item__frame {
      --bf-data-table-padding-vertical: 14px;
      --bf-data-table-padding-horizontal: 18px;
    }

    .table-item--compact tbody td,
    .table-item--compact .bf-cell-name__title {
      font-size: var(--bf-fs-sm);
    }

    /* Summary rows opt out of card stacking — there is no column left to align to, so
       they read as a label/amount list under the cards. `flex-direction` has to be restated:
       the data row above is a flex COLUMN (so the name cell can be hoisted with `order`), and
       without this the label and its figure stack on two lines instead of facing each other. */
    .table-item--redesign tbody tr.bf-summary-row {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
      gap: 20px;
      margin: 0;
      padding: 9px 2px;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      background: transparent;
    }

    /* `width` and `margin` are reset too: the stacked-card rules give every cell a full-width
       box and the action cell a top margin, and both belong to a card, not to a summary line. */
    .table-item--redesign tbody tr.bf-summary-row td {
      display: block;
      width: auto;
      margin: 0;
      padding: 0;
      border: 0;
      text-align: left;
    }

    .table-item--redesign tbody tr.bf-summary-row td::before {
      display: none;
    }

    .table-item--redesign tbody tr.bf-summary-row td.bf-td-number {
      text-align: right;
    }

    .table-item--redesign tbody tr.bf-summary-row--first {
      margin-top: 4px;
    }

    /* Stacked, the fill moves to the row itself — the cells inside it have no chrome
       left to carry it. */
    .table-item--redesign tbody tr.bf-summary-row--total {
      margin-top: 6px;
      padding: 12px 14px;
      background: var(--bf-blue-light);
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg);
    }

    .table-item--redesign tbody tr.bf-summary-row--accent {
      background: var(--bf-orange-ultralight);
    }

    .table-item--redesign tbody tr.bf-summary-row--total td,
    .table-item--redesign tbody tr.bf-summary-row--total:hover td {
      background: transparent;
    }

    /* Action cell (empty header) sits as a right-aligned card footer */
    .table-item--redesign tbody td[data-label='']::before {
      display: none;
    }

    .table-item--redesign tbody td[data-label=''] {
      justify-content: flex-end;
      margin-top: 4px;
      border-top: 1px solid var(--bf-grey-light);
      padding: 12px 16px;
    }
  }
</style>
